/*
 * Eufra product-page behaviour (lash pages, Starterpack, Fixer, Cleanser):
 * scroll sliders, the "Losse set / Starterpack" choice, the sticky add-to-cart bar,
 * one-video-with-sound, and small "animate when in view" hooks.
 */
(() => {
  'use strict';

  /*
   * Slider: [data-eu-slider] > [data-eu-slider-track] (horizontal scroll-snap container).
   * Optional controls anywhere inside the root:
   *   [data-eu-slider-prev] / [data-eu-slider-next]   arrows (hidden at the ends with .is-hidden)
   *   [data-eu-slider-count]                          "2 / 10"
   *   [data-eu-slider-thumb]                          progress thumb (left/width in %)
   *   [data-eu-slider-dot] / [data-eu-slider-to="i"]  dots and tabs (.is-active + aria-selected)
   *   [data-eu-slider-text="i"]                       text panels, only the active one is shown
   *   [data-eu-slider-hint]                           "SWIPE" badge, hidden after the first scroll
   */
  const sliders = new WeakSet();

  function slidesOf(track) {
    return [...track.children].filter((el) => !el.hasAttribute('data-eu-slider-spacer') && !el.hidden);
  }

  function activeIndex(track) {
    const slides = slidesOf(track);
    const start = track.getBoundingClientRect().left + parseFloat(getComputedStyle(track).paddingLeft || 0);
    let best = 0;
    let bestDist = Infinity;
    slides.forEach((slide, i) => {
      const d = Math.abs(slide.getBoundingClientRect().left - start);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    // at the very end the last slide may never reach the start edge
    if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 4) best = slides.length - 1;
    return best;
  }

  function goTo(root, index, smooth = true) {
    const track = root.querySelector('[data-eu-slider-track]');
    const slides = slidesOf(track);
    const slide = slides[Math.max(0, Math.min(slides.length - 1, index))];
    if (!slide) return;
    const pad = parseFloat(getComputedStyle(track).paddingLeft || 0);
    const left = slide.offsetLeft - track.offsetLeft - pad;
    track.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
  }

  function update(root) {
    const track = root.querySelector('[data-eu-slider-track]');
    const slides = slidesOf(track);
    const n = slides.length;
    const i = activeIndex(track);
    if (root.dataset.euSliderIndex === String(i)) return;
    root.dataset.euSliderIndex = String(i);

    root.querySelectorAll('[data-eu-slider-count]').forEach((el) => (el.textContent = `${i + 1} / ${n}`));
    root.querySelectorAll('[data-eu-slider-thumb]').forEach((el) => {
      el.style.width = `${100 / n}%`;
      el.style.left = `${(100 / n) * i}%`;
    });
    root.querySelectorAll('[data-eu-slider-dot]').forEach((el, k) => el.classList.toggle('is-active', k === i));
    root.querySelectorAll('[data-eu-slider-to]').forEach((el) => {
      const on = Number(el.dataset.euSliderTo) === i;
      el.classList.toggle('is-active', on);
      if (el.getAttribute('role') === 'tab') el.setAttribute('aria-selected', String(on));
    });
    root.querySelectorAll('[data-eu-slider-text]').forEach((el) => (el.hidden = Number(el.dataset.euSliderText) !== i));
    root.querySelectorAll('[data-eu-slider-prev]').forEach((el) => el.classList.toggle('is-hidden', i === 0));
    root.querySelectorAll('[data-eu-slider-next]').forEach((el) => el.classList.toggle('is-hidden', i >= n - 1));
    slides.forEach((slide, k) => slide.classList.toggle('is-current', k === i));

    // thumbnails under the gallery follow the visible slides in order
    const thumbs = [...root.querySelectorAll('[data-eu-thumb]')].filter((el) => !el.hidden);
    thumbs.forEach((el, k) => {
      el.classList.toggle('is-active', k === i);
      if (k === i) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });
    const strip = root.querySelector('[data-eu-thumbs]');
    const current = thumbs[i];
    if (strip && current && strip.scrollWidth > strip.clientWidth) {
      const offset = current.getBoundingClientRect().left - strip.getBoundingClientRect().left;
      strip.scrollTo({ left: strip.scrollLeft + offset - (strip.clientWidth - current.offsetWidth) / 2, behavior: 'smooth' });
    }

    // a playing gallery video stops when you swipe away from it
    slides.forEach((slide, k) => {
      if (k === i) return;
      slide.querySelectorAll('video[data-eu-gallery-video]').forEach((video) => {
        if (!video.paused) video.pause();
        slide.classList.remove('is-playing');
      });
    });
  }

  function initSlider(root) {
    if (sliders.has(root)) return;
    const track = root.querySelector('[data-eu-slider-track]');
    if (!track) return;
    sliders.add(root);

    let raf = 0;
    track.addEventListener(
      'scroll',
      () => {
        root.querySelectorAll('[data-eu-slider-hint]').forEach((el) => el.classList.add('is-hidden'));
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => update(root));
      },
      { passive: true }
    );

    root.addEventListener('click', (event) => {
      const prev = event.target.closest('[data-eu-slider-prev]');
      const next = event.target.closest('[data-eu-slider-next]');
      const to = event.target.closest('[data-eu-slider-to], [data-eu-slider-dot]');
      const thumb = event.target.closest('[data-eu-thumb]');
      if (thumb) {
        goTo(root, [...root.querySelectorAll('[data-eu-thumb]')].filter((el) => !el.hidden).indexOf(thumb));
        return;
      }
      if (!prev && !next && !to) return;
      if (to && to.closest('[data-eu-slider]') !== root) return;
      const i = activeIndex(track);
      if (prev) goTo(root, i - 1);
      else if (next) goTo(root, i + 1);
      else if (to.hasAttribute('data-eu-slider-to')) goTo(root, Number(to.dataset.euSliderTo));
      else goTo(root, [...root.querySelectorAll('[data-eu-slider-dot]')].indexOf(to));
    });

    // drag to scroll with a mouse (touch scrolls natively)
    let drag = null;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('[role="slider"], button, a, input')) return;
      drag = { x: event.clientX, left: track.scrollLeft, moved: false, start: activeIndex(track) };
      track.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const dx = event.clientX - drag.x;
      if (Math.abs(dx) > 4) drag.moved = true;
      track.scrollLeft = drag.left - dx;
    });
    window.addEventListener('pointerup', (event) => {
      if (!drag) return;
      const dx = event.clientX - drag.x;
      const start = drag.start;
      const moved = drag.moved;
      drag = null;
      track.classList.remove('is-dragging');
      if (moved) {
        const swallow = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };
        track.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => track.removeEventListener('click', swallow, { capture: true }), 0);
        goTo(root, Math.abs(dx) > 40 ? start + (dx < 0 ? 1 : -1) : start);
      }
    });
    track.addEventListener('dragstart', (event) => event.preventDefault());

    update(root);
  }

  /*
   * "Kies je set": [data-eu-set] with option buttons [data-eu-set-option] carrying
   * data-items (JSON for window.eufraCart.add), data-price (formatted) and data-pack ("true"/"false").
   * Every [data-eu-set-add] button on the page follows the current choice; the section gets .is-pack.
   */
  function setChoice(root, option) {
    root.querySelectorAll('[data-eu-set-option]').forEach((el) => el.setAttribute('aria-pressed', String(el === option)));
    const pack = option.dataset.pack === 'true';
    const scope = root.closest('[data-eu-set-scope]') || root;
    scope.classList.toggle('is-pack', pack);
    document.querySelectorAll('[data-eu-set-add]').forEach((button) => {
      button.dataset.euAddItems = option.dataset.items;
      button.querySelectorAll('[data-eu-set-price]').forEach((el) => (el.textContent = option.dataset.price));
      if (button.dataset.soldOut !== undefined) button.disabled = option.dataset.available === 'false';
    });
    if (option.dataset.spec) document.querySelectorAll('[data-eu-set-spec]').forEach((el) => (el.textContent = option.dataset.spec));
    document.querySelectorAll('[data-eu-set-toggle]').forEach((el) => el.classList.toggle('is-pack', pack));
    showPackSlides(scope, option.dataset.handle);
    document.dispatchEvent(new CustomEvent('eu:set-change', { detail: { pack } }));
  }

  // Starterpack gallery: slides with data-eu-pack only show for the chosen style
  function showPackSlides(scope, handle) {
    const packSlides = scope.querySelectorAll('[data-eu-pack]');
    if (!handle || !packSlides.length) return;
    let changed = false;
    packSlides.forEach((slide) => {
      const hide = slide.dataset.euPack !== handle;
      if (slide.hidden !== hide) changed = true;
      slide.hidden = hide;
      if (hide) slide.querySelectorAll('video').forEach((video) => video.pause());
    });
    if (!changed) return;
    scope.querySelectorAll('[data-eu-slider]').forEach((root) => {
      const track = root.querySelector('[data-eu-slider-track]');
      if (track) track.scrollLeft = 0;
      delete root.dataset.euSliderIndex;
      update(root);
    });
  }

  // Starterpack options carry data-eu-set-url: every style has its own page, so another style navigates there
  function goToStyle(option) {
    const url = option?.dataset.euSetUrl;
    if (!url || option.getAttribute('aria-pressed') === 'true' || window.Shopify?.designMode) return false;
    // keep the language folder (/nl, /de, /fr) of the current page
    const prefix = window.location.pathname.split('/products/')[0];
    window.location.href = url.startsWith(prefix + '/') ? url : prefix + url;
    return true;
  }

  // 'Kies Daily Charm voor je set' buttons elsewhere on the page pick an option by product handle
  document.addEventListener('click', (event) => {
    const pick = event.target.closest('[data-eu-set-pick]');
    if (!pick) return;
    const option = document.querySelector('[data-eu-set-option][data-handle="' + pick.dataset.euSetPick + '"]');
    if (goToStyle(option)) return event.preventDefault();
    const root = option?.closest('[data-eu-set]');
    if (root) setChoice(root, option);
  });

  document.addEventListener('click', (event) => {
    const option = event.target.closest('[data-eu-set-option]');
    if (!option) return;
    if (goToStyle(option)) return;
    const root = option.closest('[data-eu-set]');
    if (root) setChoice(root, option);
  });

  /*
   * Sticky add-to-cart bar (mobile): [data-eu-sticky-atc] shows once [data-eu-sticky-anchor]
   * has scrolled above the viewport, and hides again near the footer.
   */
  function initSticky() {
    const bar = document.querySelector('[data-eu-sticky-atc]');
    const anchor = document.querySelector('[data-eu-sticky-anchor]');
    if (!bar || !anchor) return;
    const footer = document.querySelector('.eu-footer, footer');
    let ticking = false;
    const check = () => {
      ticking = false;
      const past = anchor.getBoundingClientRect().bottom < 0;
      const nearEnd = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const show = past && !nearEnd;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.inert = !show;
      document.documentElement.classList.toggle('eu-has-sticky-atc', show);
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(check);
        }
      },
      { passive: true }
    );
    check();
  }

  /*
   * Video sound: [data-eu-sound] toggles the sound of the video inside the closest [data-eu-media].
   * Only one video on the page plays with sound at a time.
   */
  function muteOthers(except) {
    document.querySelectorAll('video').forEach((video) => {
      if (video === except || video.muted) return;
      video.muted = true;
      const media = video.closest('[data-eu-media]');
      media?.querySelectorAll('[data-eu-sound]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      if (video.hasAttribute('data-eu-gallery-video')) {
        video.pause();
        media?.classList.remove('is-playing');
      }
    });
  }

  document.addEventListener('click', (event) => {
    const sound = event.target.closest('[data-eu-sound]');
    if (sound) {
      const video = sound.closest('[data-eu-media]')?.querySelector('video');
      if (!video) return;
      const on = video.muted;
      if (on) muteOthers(video);
      video.muted = !on;
      if (on && video.paused) {
        video.play().catch(() => {});
        // gallery video: hide the big play button once it runs
        if (video.hasAttribute('data-eu-gallery-video')) sound.closest('[data-eu-media]').classList.add('is-playing');
      }
      sound.setAttribute('aria-pressed', String(on));
      return;
    }

    // Gallery video: big play button starts it with sound, tapping the video pauses it
    const play = event.target.closest('[data-eu-play]');
    const media = event.target.closest('[data-eu-media]');
    const video = media?.querySelector('video[data-eu-gallery-video]');
    if (!video || (!play && !media.classList.contains('is-playing'))) return;
    if (video.paused) {
      muteOthers(video);
      video.muted = false;
      const syncSound = () => media.querySelectorAll('[data-eu-sound]').forEach((b) => b.setAttribute('aria-pressed', String(!video.muted)));
      video.play().catch(() => {
        video.muted = true;
        syncSound();
        video.play().catch(() => {});
      });
      syncSound();
      media.classList.add('is-playing');
    } else {
      video.pause();
      media.classList.remove('is-playing');
    }
  });

  /*
   * Tab roots ([data-eu-tabs], see eufra.js): [data-eu-step="1|-1"] buttons go to the next/previous tab,
   * and data-eu-autoplay="3600" advances while the root is on screen until the visitor interacts.
   */
  const tabCount = (root) => new Set([...root.querySelectorAll('[data-eu-tab]')].map((t) => t.dataset.euTab)).size;
  const stepTab = (root, step) => {
    const n = tabCount(root);
    if (!n || !window.eufraSelectTab) return;
    window.eufraSelectTab(root, (Number(root.dataset.euActive || 0) + step + n) % n);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-eu-step]');
    const root = button?.closest('[data-eu-tabs]');
    if (root) stepTab(root, Number(button.dataset.euStep));
  });

  function initAutoplay(root) {
    const ms = Number(root.dataset.euAutoplay);
    if (!ms || root.dataset.euAutoplayReady) return;
    root.dataset.euAutoplayReady = '1';
    let visible = false;
    let stopped = false;
    const stop = () => (stopped = true);
    root.addEventListener('pointerdown', stop);
    root.addEventListener('keydown', stop);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => (visible = entries[0].isIntersecting), { threshold: 0.4 }).observe(root);
    }
    setInterval(() => {
      if (visible && !stopped && !document.hidden) stepTab(root, 1);
    }, ms);
  }

  /* Quantity stepper: [data-eu-qty] inside [data-eu-qty-scope] sets the quantity of every add button in that scope */
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-eu-qty-step]');
    if (!step) return;
    const scope = step.closest('[data-eu-qty-scope]');
    const value = step.closest('[data-eu-qty]').querySelector('[data-eu-qty-value]');
    const qty = Math.max(1, Math.min(10, Number(value.textContent) + Number(step.dataset.euQtyStep)));
    value.textContent = String(qty);
    scope?.querySelectorAll('[data-eu-add-items]').forEach((button) => {
      try {
        const items = JSON.parse(button.dataset.euAddItems).map((item) => ({ ...item, quantity: qty }));
        button.dataset.euAddItems = JSON.stringify(items);
      } catch (e) {}
    });
  });

  /* Helpcentrum: search box + category chips filter [data-eu-fs-item] questions */
  function filterFaq(root) {
    const input = root.querySelector('[data-eu-fs-input]');
    const q = (input?.value || '').trim().toLowerCase();
    const active = root.querySelector('[data-eu-fs-cat][aria-pressed="true"]');
    const cat = active ? active.dataset.euFsCat : '';
    let shown = 0;
    root.querySelectorAll('[data-eu-fs-item]').forEach((item) => {
      const match = (!cat || item.dataset.cat === cat) && (!q || item.textContent.toLowerCase().includes(q));
      item.hidden = !match;
      if (match) shown++;
    });
    const count = root.querySelector('[data-eu-fs-count]');
    if (count) count.textContent = `${shown} ${shown === 1 ? count.dataset.one : count.dataset.many}`;
    const empty = root.querySelector('[data-eu-fs-empty]');
    if (empty) empty.hidden = shown > 0;
  }

  document.addEventListener('input', (event) => {
    const root = event.target.closest?.('[data-eu-faq-search]');
    if (root && event.target.matches('[data-eu-fs-input]')) filterFaq(root);
  });

  document.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-eu-fs-cat]');
    if (!chip) return;
    const root = chip.closest('[data-eu-faq-search]');
    root.querySelectorAll('[data-eu-fs-cat]').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
    filterFaq(root);
  });

  /* [data-eu-inview]: adds .is-inview once 40% of the element is visible (callouts, bars) */
  const inview =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-inview');
              inview.unobserve(entry.target);
            }),
          { threshold: 0.4 }
        )
      : null;

  function init(scope = document) {
    scope.querySelectorAll('[data-eu-slider]').forEach(initSlider);
    scope.querySelectorAll('[data-eu-inview]').forEach((el) => (inview ? inview.observe(el) : el.classList.add('is-inview')));
    scope.querySelectorAll('[data-eu-autoplay]').forEach(initAutoplay);
  }

  const start = () => {
    init();
    initSticky();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  /*
   * Photo lightbox: clicking a photo slide in a [data-eu-lightbox] gallery opens it full screen.
   * Browse with the arrows, arrow keys or a swipe; click/tap the photo to zoom in and move the
   * mouse/finger to pan. Labels come from data-eu-lb-* on the gallery (translated in Liquid).
   */
  let lb = null;
  const lbState = { photos: [], index: 0, opener: null };

  const bestSrc = (img) => {
    const set = img.getAttribute('srcset');
    if (!set) return img.currentSrc || img.src;
    return set
      .split(',')
      .map((part) => part.trim().split(/\s+/))
      .sort((a, b) => parseInt(b[1] || 0, 10) - parseInt(a[1] || 0, 10))[0][0];
  };

  function buildLightbox(gallery) {
    const d = gallery.dataset;
    const el = document.createElement('div');
    el.className = 'eu-lb';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.hidden = true;
    const arrow = (dir) =>
      `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="${dir < 0 ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}"/></svg>`;
    el.innerHTML = `
      <div class="eu-lb__stage"><img class="eu-lb__img" alt=""></div>
      <button type="button" class="eu-lb__btn eu-lb__close" aria-label="${d.euLbClose || 'Close'}"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
      <button type="button" class="eu-lb__btn eu-lb__prev" aria-label="${d.euLbPrev || 'Previous'}">${arrow(-1)}</button>
      <button type="button" class="eu-lb__btn eu-lb__next" aria-label="${d.euLbNext || 'Next'}">${arrow(1)}</button>
      <span class="eu-lb__count" aria-live="polite"></span>`;
    document.body.appendChild(el);

    const stage = el.querySelector('.eu-lb__stage');
    const img = el.querySelector('.eu-lb__img');
    const setOrigin = (x, y) => {
      const r = img.getBoundingClientRect();
      const px = Math.min(100, Math.max(0, ((x - r.left) / r.width) * 100));
      const py = Math.min(100, Math.max(0, ((y - r.top) / r.height) * 100));
      img.style.transformOrigin = `${px}% ${py}%`;
    };

    el.querySelector('.eu-lb__close').addEventListener('click', closeLightbox);
    el.querySelector('.eu-lb__prev').addEventListener('click', () => showPhoto(lbState.index - 1));
    el.querySelector('.eu-lb__next').addEventListener('click', () => showPhoto(lbState.index + 1));
    // click on the dark backdrop closes, click on the photo zooms
    stage.addEventListener('click', (event) => {
      if (event.target !== img) return closeLightbox();
      if (el.classList.contains('is-zoomed')) return el.classList.remove('is-zoomed');
      setOrigin(event.clientX, event.clientY);
      el.classList.add('is-zoomed');
    });
    stage.addEventListener('mousemove', (event) => {
      if (el.classList.contains('is-zoomed')) setOrigin(event.clientX, event.clientY);
    });

    // touch: swipe to browse, or pan while zoomed
    let touch = null;
    stage.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) return (touch = null);
        touch = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      },
      { passive: true }
    );
    stage.addEventListener(
      'touchmove',
      (event) => {
        if (touch && el.classList.contains('is-zoomed') && event.touches.length === 1) {
          event.preventDefault();
          setOrigin(event.touches[0].clientX, event.touches[0].clientY);
        }
      },
      { passive: false }
    );
    stage.addEventListener('touchend', (event) => {
      if (!touch || el.classList.contains('is-zoomed')) return;
      const dx = event.changedTouches[0].clientX - touch.x;
      const dy = event.changedTouches[0].clientY - touch.y;
      touch = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        event.preventDefault();
        showPhoto(lbState.index + (dx < 0 ? 1 : -1));
      }
    });

    document.addEventListener('keydown', (event) => {
      if (el.hidden) return;
      if (event.key === 'Escape') closeLightbox();
      else if (event.key === 'ArrowLeft') showPhoto(lbState.index - 1);
      else if (event.key === 'ArrowRight') showPhoto(lbState.index + 1);
      else if (event.key === 'Tab') {
        // keep focus inside the dialog
        const buttons = [...el.querySelectorAll('button')].filter((b) => b.offsetParent);
        const i = buttons.indexOf(document.activeElement);
        event.preventDefault();
        buttons[(i + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length]?.focus();
      }
    });
    return el;
  }

  function showPhoto(i) {
    const n = lbState.photos.length;
    if (!n) return;
    lbState.index = (i + n) % n;
    const source = lbState.photos[lbState.index];
    const img = lb.querySelector('.eu-lb__img');
    lb.classList.remove('is-zoomed');
    img.style.transformOrigin = '';
    img.src = bestSrc(source);
    img.alt = source.alt || '';
    lb.setAttribute('aria-label', source.alt || '');
    lb.querySelector('.eu-lb__count').textContent = n > 1 ? `${lbState.index + 1} / ${n}` : '';
    lb.classList.toggle('is-single', n < 2);
  }

  function openLightbox(gallery, slide) {
    // only the photos that are currently shown (starterpacks hide the other styles)
    const slides = [...gallery.querySelectorAll('.eu-pdp__slide--image')].filter((s) => !s.hidden && s.querySelector('img'));
    lbState.photos = slides.map((s) => s.querySelector('img'));
    if (!lbState.photos.length) return;
    lb = lb || buildLightbox(gallery);
    lbState.opener = document.activeElement;
    lb.hidden = false;
    document.documentElement.classList.add('eu-lb-open');
    showPhoto(Math.max(0, slides.indexOf(slide)));
    lb.querySelector('.eu-lb__close').focus({ preventScroll: true });
  }

  function closeLightbox() {
    if (!lb || lb.hidden) return;
    lb.hidden = true;
    lb.classList.remove('is-zoomed');
    document.documentElement.classList.remove('eu-lb-open');
    lbState.opener?.focus?.({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    const slide = event.target.closest('.eu-pdp__slide--image');
    const gallery = slide?.closest('[data-eu-lightbox]');
    if (!gallery || window.Shopify?.designMode) return;
    event.preventDefault();
    openLightbox(gallery, slide);
  });

  // photo slides are focusable so keyboard users can open the lightbox too
  const markZoomable = () =>
    document.querySelectorAll('[data-eu-lightbox] .eu-pdp__slide--image').forEach((slide) => {
      if (slide.hasAttribute('tabindex')) return;
      slide.setAttribute('tabindex', '0');
      slide.setAttribute('role', 'button');
      slide.setAttribute('aria-label', `${slide.closest('[data-eu-lightbox]').dataset.euLbZoom || 'Zoom'}: ${slide.querySelector('img')?.alt || ''}`);
    });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const slide = event.target.closest?.('[data-eu-lightbox] .eu-pdp__slide--image');
    if (!slide) return;
    event.preventDefault();
    openLightbox(slide.closest('[data-eu-lightbox]'), slide);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', markZoomable);
  else markZoomable();
  document.addEventListener('shopify:section:load', markZoomable);
})();
